import React, { FC } from 'react';
import Badge from '@atlaskit/badge';
import { DynamicTableStateless as DynamicTable } from '@atlaskit/dynamic-table';
import { css } from '@emotion/core';
import { SlideDown } from 'react-slidedown';
import 'react-slidedown/lib/slidedown.css';
import { token } from '@atlaskit/tokens';
import ChevronUpIcon from '@atlaskit/icon/glyph/chevron-up';
import pluralize from 'pluralize';

import {
  alignItemsCenter,
  BREACH_COLOR,
  BURN_COLOR,
  INCIDENT_COLOR,
  flex,
  grid,
  spaceGapMinimum,
  SPACE_COMPACT,
  SPACE_COZY,
  threeColumnGrid,
  eventTableStyles,
  BREACH_LIGHT_COLOR,
  BURN_LIGHT_COLOR,
} from '@/styles';
import { fields as fieldConsts } from '@/components/primitive/editable-report/constants';
import { formatDateForDisplay } from '@/utils';
import { DateDisplayFormats } from '@/constants';
import { DataPointsEventsTotals } from '@/ui';
import { selectedDateLabels } from '@/utils/date';
import { localNumberFormat } from '@/utils/string';
import { Incidents } from '@/components/composed';
import { FCC } from '@/utils/types';
import { ItemHeader } from '@/components/primitive';

import {
  formatErrorBudget,
  formatPerformanceResult,
  getDailyPerformance,
  getEventTypes,
} from './slo-card/utils';
import EventAnnotations from './event-annotations';
import { getBurnEventType } from './new-slo-views/common/utils';
import EventsBadge from './slo-card/graph/tooltip/events-badge';

type InfoPanelProps = {
  open?: boolean;
  payload: DataPointsEventsTotals & { rollup: number };
  onClose: () => void;
};

const RightAlign: FCC = props => (
  <div
    css={css`
      text-align: right;
      margin-left: ${SPACE_COZY}px;
    `}
    {...props}
  />
);

const dateColumnStyles = css`
  white-space: nowrap;
`;

const tooltipTableStyles = [
  flex,
  alignItemsCenter,
  spaceGapMinimum,
  css`
    strong {
      font-weight: 400;
    }
  `,
];

type EventTableProps = {
  events: any[];
  fields: any[];
  backgroundColor: string;
  captionLabel: string;
  useWithSfxIcon?: boolean;
  useLowTrafficTag?: boolean;
  isBold?: boolean;
  textColor?: string;
};

// Render an events table for one of the breach/burn types
const EventTable: FC<EventTableProps> = ({
  events,
  fields,
  backgroundColor,
  captionLabel,
  useWithSfxIcon = false,
  textColor = token('color.text.inverse', 'white'),
  useLowTrafficTag = false,
  isBold = false,
}: EventTableProps) => (
  <span css={eventTableStyles}>
    <DynamicTable
      caption={
        <div css={tooltipTableStyles}>
          <EventsBadge
            count={events.length}
            backgroundColor={backgroundColor}
            textColor={textColor}
            caption={captionLabel}
            useWithSfxIcon={useWithSfxIcon}
            useLowTrafficTag={useLowTrafficTag}
            showLowTrafficTooltip
            isBold={isBold}
          />
        </div>
      }
      head={{ cells: fields }}
      rows={events.map(row => ({
        cells: fields.map(({ key }) => {
          if (key === 'type') {
            return {
              key: row[key],
              content: getBurnEventType(row),
            };
          }

          if (key === 'annotation') {
            return {
              key,
              content: <EventAnnotations event={row} />,
            };
          }

          const date = row[key];
          return {
            key: date?.getTime(),
            content: date ? (
              <span css={dateColumnStyles}>
                {formatDateForDisplay(date, DateDisplayFormats.TIME_WITH_NUMERIC_DATE)}
              </span>
            ) : (
              'Ongoing'
            ),
          };
        }),
      }))}
    />
  </span>
);

export const InfoPanel: FC<InfoPanelProps> = ({ open, payload, onClose }) => {
  const {
    timestamp,
    goodCount,
    badCount,
    rollup,
    result,
    dailyGood = 0,
    dailyBad = 0,
    events: allEvents,
    incidents = [],
  } = payload;

  const { timestampLabel, rangeLabel } = selectedDateLabels(
    timestamp ? new Date(timestamp) : new Date()
  );

  const {
    breachEvents,
    burnEvents,
    sfxBreachEvents,
    sfxBurnEvents,
    lowTrafficBreachEvents,
    lowTrafficBurnEvents,
    lowTrafficSFxBreachEvents,
    lowTrafficSFxBurnEvents,
  } = getEventTypes(allEvents);

  const dailyPerformance = getDailyPerformance(dailyGood, dailyBad);

  const infoPanelResults = css`
    display: grid;
    grid-template-columns: 1fr min-content;
    align-items: baseline;
    gap: ${SPACE_COMPACT}px;
    margin-top: ${SPACE_COMPACT}px;

    & h6 {
      margin: 0;
    }
  `;

  // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
  const boxShadow = css`
    box-shadow: 0 1px 3px rgb(0 0 0 / 0.2);
  `;
  const padding = css`
    padding: 0 50px 20px 50px;
  `;

  const centerIcon = css`
    display: block;
    margin-left: auto;
    margin-right: auto;
    width: fit-content;
  `;

  const panelStyles = css`
    margin-top: 40px;
  `;

  const fields = fieldConsts;
  const incidentsCount: number = incidents.length;

  return (
    <SlideDown className="info-panel-slidedown">
      {open && (
        <div css={[boxShadow, padding, panelStyles]}>
          <div css={[centerIcon]} onClick={onClose}>
            <ChevronUpIcon label="Close panel" size="large" />
          </div>
          <div css={[grid, threeColumnGrid]}>
            <div>
              <h3>{timestampLabel}</h3>
              <div css={[infoPanelResults]}>
                <ItemHeader>Performance</ItemHeader>
                <RightAlign>{formatPerformanceResult(dailyPerformance)}%</RightAlign>
                <ItemHeader>Good Events</ItemHeader>
                <RightAlign>{localNumberFormat(dailyGood)}</RightAlign>
                <ItemHeader>Bad Events</ItemHeader>
                <RightAlign>{localNumberFormat(dailyBad)}</RightAlign>
              </div>
            </div>
            <div />
            <div>
              <h3>{rangeLabel}</h3>
              <div css={[infoPanelResults]}>
                <ItemHeader>Performance</ItemHeader>
                <RightAlign>{formatPerformanceResult(rollup)}%</RightAlign>
                <ItemHeader>Error Budget</ItemHeader>
                <RightAlign>{formatErrorBudget(result, { truncate: false })}%</RightAlign>
                <ItemHeader>Good Events</ItemHeader>
                <RightAlign>{localNumberFormat(goodCount)}</RightAlign>
                <ItemHeader>Bad Events</ItemHeader>
                <RightAlign>{localNumberFormat(badCount)}</RightAlign>
              </div>
            </div>
          </div>
          {incidentsCount > 0 && (
            <Incidents
              caption={
                <div css={tooltipTableStyles}>
                  <Badge
                    style={{
                      backgroundColor: INCIDENT_COLOR,
                      color: token('color.text.inverse', 'white'),
                    }}
                  >
                    {incidentsCount}
                  </Badge>
                  <span>Associated {pluralize('Incident', incidentsCount)}</span>
                  <span>{String.fromCodePoint(0x1f525)}</span>
                </div>
              }
              isLoading={false}
              data={incidents}
            />
          )}
          {!!breachEvents.length && (
            <EventTable
              events={breachEvents}
              fields={fields.breaches}
              backgroundColor={BREACH_COLOR}
              isBold
              captionLabel={`SLO ${pluralize('Breach', breachEvents.length)}`}
            />
          )}
          {!!lowTrafficBreachEvents.length && (
            <EventTable
              events={lowTrafficBreachEvents}
              fields={fields.breaches}
              backgroundColor={BREACH_LIGHT_COLOR}
              textColor={BREACH_COLOR}
              captionLabel={`SLO ${pluralize('Breach', lowTrafficBreachEvents.length)}`}
              useLowTrafficTag
            />
          )}
          {!!burnEvents.length && (
            <EventTable
              events={burnEvents}
              fields={fields.burnRateAlerts}
              backgroundColor={BURN_COLOR}
              isBold
              captionLabel={`Error Budget ${pluralize('Burn', burnEvents.length)}`}
            />
          )}
          {!!lowTrafficBurnEvents.length && (
            <EventTable
              events={lowTrafficBurnEvents}
              fields={fields.burnRateAlerts}
              backgroundColor={BURN_LIGHT_COLOR}
              textColor={BURN_COLOR}
              captionLabel={`Error Budget ${pluralize('Burn', lowTrafficBurnEvents.length)}`}
              useLowTrafficTag
            />
          )}
          {!!sfxBreachEvents.length && (
            <EventTable
              events={sfxBreachEvents}
              fields={fields.sfxBreaches}
              backgroundColor={BREACH_COLOR}
              isBold
              captionLabel={`Breach ${pluralize('Alert', sfxBreachEvents.length)}`}
              useWithSfxIcon
            />
          )}
          {!!lowTrafficSFxBreachEvents.length && (
            <EventTable
              events={lowTrafficSFxBreachEvents}
              fields={fields.sfxBreaches}
              backgroundColor={BREACH_LIGHT_COLOR}
              textColor={BREACH_COLOR}
              captionLabel={`Breach ${pluralize('Alert', lowTrafficSFxBreachEvents.length)}`}
              useLowTrafficTag
              useWithSfxIcon
            />
          )}
          {!!sfxBurnEvents.length && (
            <EventTable
              events={sfxBurnEvents}
              fields={fields.sfxBurnsExtended}
              backgroundColor={BURN_COLOR}
              isBold
              captionLabel={`Burn ${pluralize('Alert', sfxBurnEvents.length)}`}
              useWithSfxIcon
            />
          )}
          {!!lowTrafficSFxBurnEvents.length && (
            <EventTable
              events={lowTrafficSFxBurnEvents}
              fields={fields.sfxBurnsExtended}
              backgroundColor={BURN_LIGHT_COLOR}
              textColor={BURN_COLOR}
              captionLabel={`Burn ${pluralize('Alert', lowTrafficSFxBurnEvents.length)}`}
              useLowTrafficTag
              useWithSfxIcon
            />
          )}
        </div>
      )}
    </SlideDown>
  );
};
